import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Button as BaseButton,
  Select as BaseSelect,
  TextArea as BaseTextArea,
  Loader,
  FileUpload as BaseFileUpload
} from '@ubeya/shared-web/components';
import { COMMENT_TYPES, ALLOWED_DOCUMENT_EXTENSIONS } from '@ubeya/shared/constants';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as api from '@ubeya/shared/services/api';
import { ReactComponent as SendIcon } from '@ubeya/shared-web/assets/chat-send.svg';
import { ReactComponent as BaseAttachedFileIcon } from '@ubeya/shared-web/assets/attached-file.svg';

const SubmitLoader = styled(Loader)`
  width: 14px;
  height: 14px;
`;

const DEFAULT_TYPE_ID = COMMENT_TYPES.find(({ slug }) => slug === 'comment__other')?.id ?? COMMENT_TYPES[0]?.id;

const Container = styled.div`
  background: ${({ theme }) => theme.colors.grey1};
  padding: 10px 12px;
  border-radius: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
  margin-bottom: 12px;
`;

const EyebrowLabel = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey4};
  margin-bottom: 6px;
`;

const StyledTextArea = styled(BaseTextArea)`
  width: 100%;
  margin-bottom: 8px;

  .textarea-autosize {
    min-height: 48px;
  }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const TypeSelect = styled(BaseSelect)`
  flex-shrink: 0;

  .select-container {
    height: 32px;
    min-width: 150px;
  }

  .select-label {
    line-height: 21px !important;
    max-width: none;
  }
`;

const IconButton = styled(BaseButton)`
  height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  > svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  > svg path {
    fill: currentColor;
  }
`;

const AttachFileUpload = styled(BaseFileUpload)`
  height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  cursor: pointer;
  padding: 5px 12px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black};
  outline: none;

  > svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  > svg path {
    fill: currentColor;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Spacer = styled.div`
  flex: 1;
`;

const FileChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey4};
  background: ${({ theme }) => theme.colors.grey2};
  padding: 3px 8px;
  border-radius: 12px;
  max-width: 180px;
`;

const FileName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveFile = styled.span`
  cursor: pointer;
  flex-shrink: 0;
  line-height: 1;
  color: ${({ theme }) => theme.colors.grey3};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const AttachedFileIcon = styled(BaseAttachedFileIcon)`
  path {
    fill: currentColor !important;
  }
`;

const AddCommentSection = ({ onSubmit }) => {
  const { t, accountId } = useContext(ConfigContext);

  const commentTypeOptions = useMemo(
    () =>
      COMMENT_TYPES.filter(
        ({ restrictToAccountIds }) => !restrictToAccountIds || restrictToAccountIds.includes(accountId)
      )
        .sort((a, b) => Number(b.slug === 'comment__eca') - Number(a.slug === 'comment__eca'))
        .map(({ id, slug }) => ({ value: id, label: t(slug) })),
    [t, accountId]
  );

  const defaultTypeId = commentTypeOptions[0]?.value ?? DEFAULT_TYPE_ID;

  const [comment, setComment] = useState('');
  const [type, setType] = useState(defaultTypeId);
  const [attachment, setAttachment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatValue = useCallback(({ displayValue }) => t('commentTypePrefix', { value: displayValue }), [t]);

  const isSubmitDisabled = !comment.trim() || isSubmitting;

  const onUploaded = useCallback(([{ file, path }]) => {
    setAttachment({ filePath: path, attachmentName: file.name });
  }, []);

  const handleRemoveFile = useCallback(() => setAttachment(null), []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitDisabled) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        comment,
        type,
        filePath: attachment?.filePath,
        attachmentName: attachment?.attachmentName
      });
      setComment('');
      setType(defaultTypeId);
      setAttachment(null);
    } finally {
      setIsSubmitting(false);
    }
  }, [comment, type, attachment, isSubmitDisabled, onSubmit, defaultTypeId]);

  return (
    <Container>
      <EyebrowLabel aria-hidden="true">{t('addAComment')}</EyebrowLabel>
      <StyledTextArea
        aria-label={t('writeCommentAboutEmployee')}
        placeholder={t('writeCommentAboutEmployee')}
        value={comment}
        onChange={setComment}
        disabled={isSubmitting}
      />
      <ActionRow>
        <TypeSelect
          value={type}
          onChange={setType}
          options={commentTypeOptions}
          formatValue={formatValue}
          searchable={false}
          usePortal
          disabled={isSubmitting}
        />
        {attachment ? (
          <FileChip>
            <FileName title={attachment.attachmentName}>{attachment.attachmentName}</FileName>
            <RemoveFile onClick={handleRemoveFile} aria-label={t('removeAttachment')}>
              ×
            </RemoveFile>
          </FileChip>
        ) : (
          <AttachFileUpload
            apiFunc={api.uploadImage}
            onUploaded={onUploaded}
            accept={ALLOWED_DOCUMENT_EXTENSIONS}
            loaderSize="xsmall">
            <AttachedFileIcon />
            {t('attachFile')}
          </AttachFileUpload>
        )}
        <Spacer />
        <IconButton
          primary
          narrow
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          aria-disabled={isSubmitDisabled || undefined}>
          {isSubmitting ? <SubmitLoader size="xsmall" thickness="xsmall" /> : <SendIcon aria-hidden="true" />}
          {t('addComment')}
        </IconButton>
      </ActionRow>
    </Container>
  );
};

export default React.memo(AddCommentSection);
